import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { GET_AD_LAUNCHES_QUERY_KEY } from "../../utils/constants/queryKeys";
import { SYNC_OUTCOME } from "../../utils/constants/AdLaunch";
import toast from "react-hot-toast";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AdsAPIs from "../../apiRoutes/Ads";
import CustomModal from "../common/CustomModal";

/** Retry Sync live for one ad set and show what happened to each waiting hook. */
const AdsetSyncRetry = ({ adRequestId, adsetName }) => {
  const [result, setResult] = useState(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () =>
      AdsAPIs.handleSyncLiveForRequest({
        axiosPrivate,
        ad_request_id: adRequestId,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: GET_AD_LAUNCHES_QUERY_KEY });
      toast.success(`${data?.marked_live ?? 0} marked live`);
      setResult(data);
    },
  });
  const metaNames = result?.meta_names || [];
  const hasNotFound = (result?.results || []).some(
    (r) => r.reason_code === "not_found"
  );

  const handleRetry = () => retry.mutate();

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={handleRetry}
        disabled={retry.isPending}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
        title="Look this ad set up on Meta again and mark live hooks live"
      >
        {retry.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" />
        )}
        Retry sync
      </button>
      {result && (
        <CustomModal
          open
          title="Retry sync"
          onClose={() => setResult(null)}
          isButtonsRequired={false}
          className="md:!w-[70%] lg:!w-[44%]"
          bodyClassName="py-4"
        >
          <div className="space-y-4 text-xs">
            <p className="text-gray-500 break-all">
              {adsetName}
              {result.account ? ` · checked ${result.account}` : ""}
            </p>
            {!result.results?.length && (
              <p className="text-sm text-gray-600">
                Nothing in this ad set is waiting to be synced.
              </p>
            )}
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {(result.results || []).map((r) => (
                <li key={r.ad_moderate_id} className="px-3 py-2">
                  <p className="font-mono text-gray-800 break-all">
                    {r.ad_name}
                  </p>
                  <p
                    className={`mt-0.5 ${SYNC_OUTCOME[r.outcome]?.tone || "text-gray-600"}`}
                  >
                    {SYNC_OUTCOME[r.outcome]?.label || r.outcome}
                    {r.reason ? `: ${r.reason}` : ""}
                  </p>
                </li>
              ))}
            </ul>
            {hasNotFound && metaNames.length > 0 && (
              <div>
                <p className="font-medium text-gray-700">
                  Other ads Meta has under this name, check for a typo:
                </p>
                {metaNames.map((name) => (
                  <p
                    key={name}
                    className="mt-1 font-mono text-gray-600 break-all"
                  >
                    {name}
                  </p>
                ))}
              </div>
            )}
            {result.ambiguous?.length > 0 && (
              <p className="text-amber-700">
                Some hooks share a name with another AMS hook, so they were
                skipped. Mark them live by hand.
              </p>
            )}
          </div>
        </CustomModal>
      )}
    </span>
  );
};

export default AdsetSyncRetry;
